// =============================================================================
// Receipt Printer Service — Web Serial API (ESC/POS over Bluetooth COM port)
// =============================================================================

// ESC/POS command constants
const ESC = 0x1b;
const GS = 0x1d;

const CMD = {
  INIT: [ESC, 0x40],                    // Initialize printer
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_SIZE: [ESC, 0x21, 0x30],       // Double width + height
  NORMAL_SIZE: [ESC, 0x21, 0x00],
  FEED_LINES: (n) => [ESC, 0x64, n],
  PARTIAL_CUT: [GS, 0x56, 0x01],
};

const RECEIPT_WIDTH = 32; // characters for 58mm thermal printer

class ReceiptPrinterServiceClass {
  constructor() {
    this.port = null;
    this.writer = null;
  }

  /**
   * Check if Web Serial is supported
   */
  isSupported() {
    return 'serial' in navigator;
  }

  /**
   * Connect to a serial port. Prompts user to pick if not already connected.
   */
  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Serial API is not supported in this browser. Use Chrome or Edge.');
    }

    // If already connected and port is readable, reuse it
    if (this.port && this.port.writable) {
      return;
    }

    // Request port — browser shows picker every time if no port is stored
    this.port = await navigator.serial.requestPort();

    // Open with standard thermal printer baud rate
    await this.port.open({ baudRate: 9600 });

    this.writer = this.port.writable.getWriter();
  }

  /**
   * Disconnect and release the port
   */
  async disconnect() {
    try {
      if (this.writer) {
        await this.writer.releaseLock();
        this.writer = null;
      }
      if (this.port) {
        await this.port.close();
        this.port = null;
      }
    } catch (e) {
      // Ignore disconnect errors
      this.writer = null;
      this.port = null;
    }
  }

  /**
   * Send raw bytes to the printer
   */
  async sendData(data) {
    if (!this.writer) {
      throw new Error('Printer not connected');
    }
    const buffer = new Uint8Array(data);
    await this.writer.write(buffer);
  }

  /**
   * Encode text to bytes
   */
  textToBytes(text) {
    const encoder = new TextEncoder();
    return Array.from(encoder.encode(text));
  }

  /**
   * Create a separator line
   */
  separator(char = '-') {
    return char.repeat(RECEIPT_WIDTH);
  }

  /**
   * Format a line with left and right aligned text
   */
  formatLine(left, right) {
    const maxLeft = RECEIPT_WIDTH - right.length - 1;
    const trimmedLeft = left.length > maxLeft ? left.substring(0, maxLeft) : left;
    const spaces = RECEIPT_WIDTH - trimmedLeft.length - right.length;
    return trimmedLeft + ' '.repeat(Math.max(1, spaces)) + right;
  }

  /**
   * Print receipt for an order.
   * If no printer is connected, prompts user to select a port.
   */
  async printReceipt(order, outletName) {
    await this.connect();

    const data = this.buildReceiptData(order, outletName);
    await this.sendData(data);
  }

  /**
   * Build ESC/POS receipt byte array
   */
  buildReceiptData(order, outletName) {
    const bytes = [];
    const push = (...args) => args.forEach((a) => {
      if (Array.isArray(a)) bytes.push(...a);
      else bytes.push(a);
    });
    const pushText = (text) => push(this.textToBytes(text + '\n'));

    // Initialize printer
    push(CMD.INIT);

    // Header — outlet name (centered, bold, double size)
    push(CMD.ALIGN_CENTER);
    push(CMD.BOLD_ON);
    push(CMD.DOUBLE_SIZE);
    pushText(outletName || 'KAROBAR');
    push(CMD.NORMAL_SIZE);
    push(CMD.BOLD_OFF);

    // Separator
    push(CMD.ALIGN_LEFT);
    pushText('');
    pushText(this.separator('='));

    // Order info
    push(CMD.BOLD_ON);
    pushText(`Order #${order.OrderNum || order.order_num || ''}`);
    push(CMD.BOLD_OFF);

    // Date
    const dateStr = order.CreatedAt || order.created_at
      ? new Date(order.CreatedAt || order.created_at).toLocaleString('en-IN', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
    pushText(`Date: ${dateStr}`);

    pushText(this.separator('-'));

    // Column headers
    push(CMD.BOLD_ON);
    pushText(this.formatLine('Item', 'Amt'));
    push(CMD.BOLD_OFF);
    pushText(this.separator('-'));

    // Items
    const items = order.items || [];
    if (items.length > 0) {
      items.forEach((item) => {
        const name = item.item_name || item.name || 'Item';
        const qty = item.quantity || 1;
        const amt = item.amount || 0;
        const itemLine = `${name} x${qty}`;
        const amtStr = `Rs.${amt.toFixed(2)}`;
        pushText(this.formatLine(itemLine, amtStr));
      });
    } else if (order.ItemNames && order.Quantity) {
      order.ItemNames.forEach((name, i) => {
        const qty = order.Quantity[i] || 1;
        const itemLine = `${name} x${qty}`;
        pushText(itemLine);
      });
    }

    pushText(this.separator('='));

    // Total
    push(CMD.BOLD_ON);
    push(CMD.DOUBLE_SIZE);
    const total = order.Amount || order.total_amount || 0;
    pushText(this.formatLine('TOTAL', `Rs.${total.toFixed(2)}`));
    push(CMD.NORMAL_SIZE);
    push(CMD.BOLD_OFF);

    pushText(this.separator('-'));

    // Footer
    push(CMD.ALIGN_CENTER);
    pushText('');
    pushText('Payment: UPI (Paid)');
    pushText('');
    pushText('With love <3 from Karobar');
    pushText('');

    // Feed and cut
    push(CMD.FEED_LINES(4));
    push(CMD.PARTIAL_CUT);

    return bytes;
  }
}

export const ReceiptPrinterService = new ReceiptPrinterServiceClass();
